import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import {
  History, RefreshCw, Search, User, Wallet, CalendarDays,
  PlusCircle, Trash2, Edit3, HandCoins,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATS = [
  { id: 'all', label: 'Tümü' },
  { id: 'muhasebe', label: 'Muhasebe' },
  { id: 'rezervasyon', label: 'Rezervasyon' },
];

const actionMeta = (action) => {
  switch (action) {
    case 'create': return { label: 'Ekleme', icon: PlusCircle, cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
    case 'delete': return { label: 'Silme', icon: Trash2, cls: 'bg-rose-100 text-rose-700 border-rose-200' };
    case 'update': return { label: 'Güncelleme', icon: Edit3, cls: 'bg-blue-100 text-blue-700 border-blue-200' };
    case 'collect': return { label: 'Tahsilat', icon: HandCoins, cls: 'bg-teal-100 text-teal-700 border-teal-200' };
    default: return { label: action || '-', icon: Edit3, cls: 'bg-slate-100 text-slate-600 border-slate-200' };
  }
};

const fmt = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const ActivityLogTab = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ items: [], count: 0, total: 0 });
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (category !== 'all') params.category = category;
      if (search.trim()) params.q = search.trim();
      const res = await axios.get(`${API}/activity-logs`, { params });
      setData(res.data || { items: [], count: 0, total: 0 });
    } catch (e) {
      setError('İşlem kayıtları yüklenemedi. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); /* eslint-disable-next-line */ }, [category]);

  return (
    <div className="space-y-4" data-testid="activity-log-tab">
      {/* Hero */}
      <div className="bg-gradient-to-r from-slate-800 via-slate-900 to-black rounded-xl p-4 text-white shadow-lg">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="bg-white/10 p-3 rounded-lg backdrop-blur">
              <History className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">İşlem Kayıtları</h2>
              <p className="text-white/70 text-sm">Muhasebe ve rezervasyon işlemleri — kim, ne zaman, ne yaptı</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-white/10 px-4 py-2 rounded-lg backdrop-blur text-center">
              <p className="text-xs text-white/60">Toplam Kayıt</p>
              <p className="text-xl font-bold">{data.total || 0}</p>
            </div>
            <Button variant="secondary" size="sm" onClick={fetchLogs} data-testid="activity-refresh-btn"
              className="bg-white/15 hover:bg-white/25 text-white border-0">
              <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Yenile
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 md:items-center">
        <div className="flex gap-2">
          {CATS.map((c) => (
            <button
              key={c.id}
              onClick={() => setCategory(c.id)}
              data-testid={`activity-filter-${c.id}`}
              className={`px-3.5 py-2 text-sm font-medium rounded-lg border transition-colors ${
                category === c.id
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            placeholder="Açıklama veya kullanıcı ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchLogs()}
            className="pl-9"
            data-testid="activity-search-input"
          />
        </div>
        <Button variant="outline" size="sm" onClick={fetchLogs}>Ara</Button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500" data-testid="activity-loading">Yükleniyor...</div>
        ) : error ? (
          <div className="p-10 text-center" data-testid="activity-error">
            <p className="text-rose-600 mb-3">{error}</p>
            <Button onClick={fetchLogs} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Tekrar Dene
            </Button>
          </div>
        ) : data.items.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="activity-empty">
            <History className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            Henüz işlem kaydı yok.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="activity-table">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Tarih & Saat</th>
                  <th className="text-left px-4 py-3 font-semibold">Kullanıcı</th>
                  <th className="text-left px-4 py-3 font-semibold">Kategori</th>
                  <th className="text-left px-4 py-3 font-semibold">İşlem</th>
                  <th className="text-left px-4 py-3 font-semibold">Açıklama</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.items.map((i) => {
                  const am = actionMeta(i.action);
                  const AmIcon = am.icon;
                  return (
                    <tr key={i.id} className="hover:bg-slate-50 transition-colors" data-testid={`activity-row-${i.id}`}>
                      <td className="px-4 py-3 whitespace-nowrap text-slate-700">
                        <span className="inline-flex items-center gap-1.5">
                          <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                          {fmt(i.created_at)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5 font-medium text-slate-800">
                          <User className="w-3.5 h-3.5 text-slate-400" />
                          {i.actor_name}
                          {i.actor_role && <span className="text-xs text-slate-400">({i.actor_role})</span>}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${
                          i.category === 'muhasebe'
                            ? 'bg-amber-100 text-amber-700 border-amber-200'
                            : 'bg-indigo-100 text-indigo-700 border-indigo-200'
                        }`}>
                          {i.category === 'muhasebe' ? <Wallet className="w-3 h-3" /> : <CalendarDays className="w-3 h-3" />}
                          {i.category === 'muhasebe' ? 'Muhasebe' : 'Rezervasyon'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${am.cls}`}>
                          <AmIcon className="w-3 h-3" /> {am.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{i.description}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLogTab;
